import { useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDockState } from '@/hooks/useDockState';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAppContext } from '@/context/AppContext';

export const useDashboardLayout = () => {
  // Use AppContext for sidebarCollapsed to persist across navigation
  const { sidebarCollapsed, setSidebarCollapsed } = useAppContext();
  // Role is constant - the setter was only fed by a now-removed
  // onRoleChange handler in Header. Downstream consumers
  // (Interface) still read it as a display string.
  const currentRole = 'Executive';
  const [isHovered, setIsHovered] = useState(false);
  const { dockMode, toggleDockMode } = useDockState();
  const isMobile = useIsMobile();
  const location = useLocation();

  // Route type detection
  const routeType = useMemo(() => {
    const pathname = location.pathname;
    
    if (pathname.startsWith('/dashboard/admin')) {
      return 'admin' as const;
    }
    if (pathname === '/dashboard/knowledge') {
      return 'knowledge' as const;
    }
    return 'default' as const;
  }, [location.pathname]);

  // Sidebar visibility logic
  const sidebarVisible = useMemo(() => 
    dockMode === 'docked' || (dockMode === 'undocked' && isHovered), 
    [dockMode, isHovered]
  );
  
  // Effective collapsed state for auto-hide behavior
  const effectiveCollapsed = useMemo(() => 
    dockMode === 'undocked' && !isHovered ? true : sidebarCollapsed,
    [dockMode, isHovered, sidebarCollapsed]
  );

  // Layout styles
  const layoutStyles = useMemo(() => ({
    sidebarContainer: {
      willChange: 'transform' as const,
      backfaceVisibility: 'hidden' as const,
      WebkitBackfaceVisibility: 'hidden' as const
    },
    contentContainer: { 
      scrollbarGutter: 'stable' as const,
      willChange: 'margin-left' as const,
      backfaceVisibility: 'hidden' as const
    },
    contentInner: {}
  }), []);

  // Event handlers
  const handleSidebarToggle = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (dockMode === 'undocked' && !isMobile) {
      setIsHovered(true);
    }
  }, [dockMode, isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (dockMode === 'undocked' && !isMobile) {
      setIsHovered(false);
    }
  }, [dockMode, isMobile]);

  return {
    // State
    sidebarCollapsed,
    currentRole,
    isHovered,
    dockMode,
    isMobile,
    location,
    routeType,
    
    // Computed values
    sidebarVisible,
    effectiveCollapsed,
    layoutStyles,
    
    // Actions
    handleSidebarToggle,
    handleMouseEnter,
    handleMouseLeave,
    toggleDockMode
  };
};